import { useEffect, useState } from "react";
import { Loader2, Crown, Mail, Trash2, Plus, X, Trophy, Wallet } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * GoatTeamPanel — visible only to GOAT plan OWNERS (tier='goat' AND no
 * owner_user_id). Lets them invite up to 20 members, see acceptance state,
 * revoke/remove, and view the rollup of team-wide bill defeats + Vault total.
 */
export default function GoatTeamPanel({ onChange }) {
  const [team, setTeam] = useState(null);
  const [rollup, setRollup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, r] = await Promise.all([
        api.get("/goat/team"),
        api.get("/goat/rollup").catch(() => ({ data: null })),
      ]);
      setTeam(t.data);
      setRollup(r.data);
      onChange?.();
    } catch (err) {
      const code = err.response?.data?.detail?.code;
      if (code === "GOAT_OWNER_ONLY") {
        // Not an owner — silently hide. Parent Upgrade page should not even
        // render this panel for non-owners but be defensive.
        setTeam(null);
      } else {
        toast.error("Couldn't load your team.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/goat/invites", { email: email.trim().toLowerCase(), role: "member" });
      setTeam(r.data);
      setEmail("");
      toast.success(`Invite sent to ${email.trim().toLowerCase()}`);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message;
      toast.error(msg || "Couldn't send invite.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m) => {
    const id = m.invite_id || m.user_id;
    if (!id) return;
    if (!window.confirm(m.status === "pending" ? `Cancel invite for ${m.email}?` : `Remove ${m.email} from your team?`)) return;
    try {
      await api.delete(`/goat/invites/${id}`);
      toast.success(m.status === "pending" ? "Invite cancelled" : "Member removed");
      load();
    } catch {
      toast.error("Couldn't remove.");
    }
  };

  if (loading && !team) {
    return (
      <div className="ht-card rounded-2xl p-6 flex items-center justify-center" data-testid="goat-team-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#22E07A]" />
      </div>
    );
  }
  if (!team) return null;

  const remaining = Math.max(0, (team.max_members || 20) - (team.used_slots || 0));
  const atCap = remaining === 0;

  return (
    <section className="ht-card rounded-2xl p-5 space-y-5" data-testid="goat-team-panel">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-[#F5D849]" strokeWidth={2.6} />
          <h3 className="text-lg font-black uppercase tracking-wider text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
            Your G.O.A.T Team
          </h3>
        </div>
        <span
          className={`text-[11px] font-extrabold uppercase tracking-widest px-2 py-1 rounded-full ${
            atCap ? "bg-[#3A2D0F] text-[#F5D849]" : "bg-[#11413B] text-[#22E07A]"
          }`}
          data-testid="goat-seat-count"
        >
          {team.used_slots}/{team.max_members} seats
        </span>
      </header>

      {rollup && rollup.member_count > 0 && (
        <div className="grid grid-cols-3 gap-2" data-testid="goat-rollup">
          <div className="rounded-xl bg-[#0A2320] border border-[#1B4238] p-3">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#93B1A8]">
              <Trophy className="w-3 h-3 text-[#F5D849]" /> Team defeats
            </div>
            <div className="text-2xl font-black text-white tabular-nums mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
              {rollup.total_bills_defeated}
            </div>
          </div>
          <div className="rounded-xl bg-[#0A2320] border border-[#1B4238] p-3">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#93B1A8]">
              <Wallet className="w-3 h-3 text-[#22E07A]" /> Reclaimed
            </div>
            <div className="text-2xl font-black text-[#22E07A] tabular-nums mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
              ${rollup.total_monthly_reclaimed.toFixed(0)}
              <span className="text-xs text-[#93B1A8] ml-1">/mo</span>
            </div>
          </div>
          <div className="rounded-xl bg-[#0A2320] border border-[#1B4238] p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#93B1A8]">Vault total</div>
            <div className="text-2xl font-black text-[#F5D849] tabular-nums mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
              ${rollup.total_vault_balance.toFixed(2)}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={invite} className="flex gap-2" data-testid="goat-invite-form">
        <Input
          type="email"
          inputMode="email"
          placeholder="Invite by email…"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy || atCap}
          required
          className="flex-1 h-11 bg-[#0A2320] text-white border-[#1B4238] focus-visible:ring-[#22E07A]/40"
          data-testid="goat-invite-email-input"
        />
        <Button
          type="submit"
          disabled={busy || atCap || !email.trim()}
          className="h-11 rounded-xl bg-[#22E07A] hover:bg-[#17C06A] text-[#05160D] font-black uppercase tracking-wider gap-1.5 px-4"
          style={{ fontFamily: "Outfit, sans-serif" }}
          data-testid="goat-invite-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Invite</>}
        </Button>
      </form>
      {atCap && (
        <p className="text-[12px] text-[#F5D849] text-center" data-testid="goat-cap-msg">
          You&apos;ve filled all 20 seats. Remove a member to invite someone new.
        </p>
      )}

      <ul className="divide-y divide-[#1B4238]" data-testid="goat-member-list">
        {(team.members || []).map((m) => (
          <li
            key={m.invite_id || m.user_id}
            className="flex items-center justify-between gap-3 py-2.5"
            data-testid={`goat-member-${m.email}`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Mail className={`w-3.5 h-3.5 ${m.status === "accepted" ? "text-[#22E07A]" : "text-[#F5D849]"}`} />
                <span className="text-[13px] font-bold text-white truncate">{m.email}</span>
              </div>
              <div className="text-[10px] uppercase tracking-wider mt-0.5">
                <span className={m.status === "accepted" ? "text-[#22E07A]" : "text-[#F5D849]"}>
                  {m.status === "accepted" ? "Active" : "Pending"}
                </span>
                <span className="text-[#5F7E76] mx-1.5">·</span>
                <span className="text-[#93B1A8]">{m.role}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => remove(m)}
              className="text-[#93B1A8] hover:text-red-400 transition-colors p-2 rounded-lg hover:bg-[#0A2320]"
              data-testid={`goat-member-remove-${m.email}`}
              aria-label={`Remove ${m.email}`}
            >
              {m.status === "pending" ? <X className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
            </button>
          </li>
        ))}
        {(team.members || []).length === 0 && (
          <li className="py-4 text-center text-[12px] text-[#93B1A8] italic">
            No members yet. Invite up to 20 family or team members.
          </li>
        )}
      </ul>
    </section>
  );
}
